/* Gull-style (light admin) primitives shared by every legacy page – polished */
import { Loader2, Inbox, X } from "lucide-react";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { t } from "@/i18n";
import { DatePicker } from "@/components/gull/DatePicker";

const ACCENT = "#663399";

export const Card = ({ title, subtitle, children, className = "", right, testId, flush = false }) => (
  <div data-testid={testId} className={`tp-fade min-w-0 max-w-full bg-white rounded-xl border border-gray-100 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.12)] ${className}`}>
    {(title || right) && (
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4">
        {title ? (
          <div>
            <h4 className="flex items-center gap-2 text-[13px] font-extrabold uppercase tracking-wider text-gray-600"><span className="w-1.5 h-4 rounded-full" style={{ background: ACCENT }} />{t(title)}</h4>
            {subtitle && <p className="text-xs text-gray-400 mt-1 ml-3.5">{t(subtitle)}</p>}
          </div>
        ) : <span />}
        {right && <div className="flex flex-wrap items-center gap-2 max-w-full [&>div]:flex-wrap [&>div]:max-w-full">{right}</div>}
      </div>
    )}
    <div className={flush ? "" : "p-4 sm:p-5"}>{children}</div>
  </div>
);

const btnColors = {
  primary: "bg-[#663399] hover:bg-[#5a2d8a] text-white shadow-sm shadow-[#663399]/25",
  outline: "border border-[#663399]/35 text-[#663399] bg-white hover:bg-[#663399]/[0.06] hover:border-[#663399]/60",
  success: "bg-[#2e9e5b] hover:bg-[#278c50] text-white shadow-sm shadow-[#2e9e5b]/25",
  info: "bg-[#0ea5c6] hover:bg-[#0c92b0] text-white shadow-sm shadow-[#0ea5c6]/25",
  danger: "bg-[#e5484d] hover:bg-[#d13c41] text-white shadow-sm shadow-[#e5484d]/25",
  warning: "bg-[#f5b400] hover:bg-[#e0a500] text-gray-900 shadow-sm",
  secondary: "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200",
  ghost: "text-gray-600 hover:bg-gray-100",
  link: "text-[#663399] hover:underline bg-transparent px-0",
};

export const Btn = ({ color = "primary", size = "md", className = "", loading, children, ...rest }) => (
  <button
    type="button"
    {...rest}
    disabled={rest.disabled || loading}
    className={`inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold whitespace-nowrap transition-[transform,box-shadow,background-color,border-color] duration-150 ease-out
      hover:-translate-y-px active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:shadow-none
      focus:outline-none focus-visible:ring-2 focus-visible:ring-[#663399]/35 focus-visible:ring-offset-1
      ${size === "sm" ? "px-3 py-1.5 text-xs" : size === "lg" ? "px-6 py-3 text-base" : "px-4 py-2 text-sm"} ${btnColors[color]} ${className}`}
  >
    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
    {t(children)}
  </button>
);

export const inputCls = "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 shadow-[inset_0_1px_2px_rgba(16,24,40,0.06)] placeholder:text-gray-400 transition-colors hover:border-gray-400 focus:outline-none focus:border-[#663399] focus:ring-2 focus:ring-[#663399]/15 disabled:bg-gray-50 disabled:text-gray-500 disabled:border-gray-100";

export const Label = ({ children }) => <span className="block text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">{t(children)}</span>;

/* Amount fields: a 0 clears itself when you click in, and an empty field goes back to 0 when you leave */
export const zeroClear = (value, onChange, extra = {}) => ({
  onFocus: (e) => { if (String(value ?? "") !== "" && Number(value) === 0) onChange({ target: { value: "", name: e.target.name } }); extra.onFocus?.(e); },
  onBlur: (e) => { if (String(e.target.value).trim() === "") onChange({ target: { value: "0", name: e.target.name } }); extra.onBlur?.(e); },
});

export const Input = ({ label, className = "", ...rest }) => {
  if (rest.type === "date") {
    const { type, value, onChange, disabled, ...r } = rest;
    return (
      <div className={`block min-w-0 ${className.split(" ").filter((c) => /^(flex-|min-w-|w-|sm:|md:|max-w-)/.test(c)).join(" ")}`}>
        {label && <Label>{label}</Label>}
        <DatePicker value={value || ""} onChange={onChange} disabled={disabled} className={`w-full ${className.split(" ").filter((c) => !/^(flex-|min-w-|w-|sm:|md:|max-w-)/.test(c)).join(" ")}`} {...r} />
      </div>
    );
  }
  const num = rest.type === "number" && rest.onChange && !rest.disabled ? zeroClear(rest.value, rest.onChange, rest) : {};
  return (
    <label className="block">
      {label && <Label>{label}</Label>}
      <input {...rest} {...num} className={`${inputCls} ${className}`} />
    </label>
  );
};

const fmtMoney = (v) => { const n = Number(v || 0); return (n < 0 ? "-$" : "$") + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };

/* Amount field: shows $1,234.56 when idle, plain digits while editing (0 clears on focus, blank returns to 0) */
export const MoneyInput = ({ label, value, onChange, disabled, allowEmpty = false, className = "", ...rest }) => {
  const [editing, setEditing] = useState(false);
  const emit = (v) => onChange({ target: { value: v, name: rest.name } });
  const blank = value === "" || value === null || value === undefined;
  const el = (
    <input {...rest} type="text" inputMode="decimal" disabled={disabled} autoComplete="off"
      value={editing ? (value ?? "") : blank ? "" : fmtMoney(value)}
      onFocus={(e) => { setEditing(true); if (!blank && Number(value) === 0) emit(""); else if (!blank) requestAnimationFrame(() => e.target.select?.()); rest.onFocus?.(e); }}
      onBlur={(e) => { setEditing(false); const empty = String(value ?? "").trim() === "" || isNaN(Number(value)); if (empty && !allowEmpty) emit("0"); else if (empty && isNaN(Number(value))) emit(""); rest.onBlur?.(e); }}
      onChange={(e) => { const v = e.target.value.replace(/[^0-9.-]/g, ""); if (/^-?\d*\.?\d{0,2}$/.test(v)) emit(v); }}
      className={`${inputCls} text-right tabular-nums ${editing ? "" : "font-semibold"} ${className}`} />
  );
  return label ? <label className="block">{label && <Label>{label}</Label>}{el}</label> : el;
};

export const Select = ({ label, children, className = "", ...rest }) => (
  <label className="block">
    {label && <Label>{label}</Label>}
    <select {...rest} className={`${inputCls} pr-8 bg-no-repeat bg-[right_0.6rem_center] bg-[length:14px] appearance-none ${className}`} style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")" }}>{children}</select>
  </label>
);

export const EmptyState = ({ text = "Nothing here yet", hint }) => (
  <div data-testid="empty-state" className="flex flex-col items-center justify-center py-10 text-center">
    <span className="w-10 h-10 rounded-full bg-gray-100 grid place-items-center text-gray-400 mb-2"><Inbox className="w-5 h-5" /></span>
    <p className="text-sm font-semibold text-gray-500">{text}</p>
    {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
  </div>
);

export const Loading = ({ text = "Loading…" }) => <p data-testid="loading" className="flex items-center gap-2 text-sm text-gray-400 py-4"><Loader2 className="w-4 h-4 animate-spin" />{text}</p>;

export const Table = ({ cols, rows, renderRow, empty = "Nothing here yet", emptyHint, testId, footer, dense = false, rowClassName }) => (
  <div className="overflow-x-auto rounded-xl border border-gray-100">
    <table data-testid={testId} className={`w-full text-sm text-gray-700 ${dense ? "[&_td]:py-1.5" : ""}`}>
      <thead className="bg-gray-50/90 text-[11px] uppercase tracking-wider text-gray-500">
        <tr>{cols.map((c, i) => <th key={typeof c === "string" ? c : i} className="px-3 py-2.5 text-left font-bold border-b border-gray-100 whitespace-nowrap">{t(c)}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={cols.length}><EmptyState text={empty} hint={emptyHint} /></td></tr>
        ) : rows.map((r, i) => (
          <tr key={r.__key ?? i} className={`border-b border-gray-50 last:border-0 transition-colors ${i % 2 ? "bg-gray-50/40" : "bg-white"} hover:bg-[#663399]/[0.04] ${rowClassName ? rowClassName(r) : ""}`}>{renderRow(r, i)}</tr>
        ))}
      </tbody>
      {footer}
    </table>
  </div>
);

export const Td = ({ children, className = "", ...rest }) => <td {...rest} className={`px-3 py-2 whitespace-nowrap ${className}`}>{children}</td>;

export const PageTitle = ({ children, right, subtitle }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 mb-4 pb-3 border-b border-gray-200/80">
    <div>
      <h1 className="text-xl md:text-2xl font-extrabold text-gray-800 tracking-tight flex items-center gap-2.5"><span className="hidden sm:block w-1.5 h-6 rounded-full" style={{ background: ACCENT }} />{t(children)}</h1>
      {subtitle && <p className="text-sm text-gray-500 mt-1 sm:ml-4">{t(subtitle)}</p>}
    </div>
    {right && <div className="flex flex-wrap items-center gap-2 max-w-full [&>div]:flex-wrap [&>div]:max-w-full">{right}</div>}
  </div>
);

/* KPI / summary tile */
export const Stat = ({ label, value, tone = "text-gray-800", testId, className = "", sub }) => (
  <div data-testid={testId} className={`bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 ${className}`}>
    <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}</div>
    <div className={`text-xl font-extrabold tabular-nums leading-tight ${tone}`}>{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

/* Rendered in a portal on <body> so it is always centred in the viewport (ancestor transforms / overflow can't move it) */
export const Modal = ({ open, title, children, onClose, testId, wide }) => {
  useEffect(() => { if (!open || !onClose) return; const h = (e) => { if (e.key === "Escape" && !e.defaultPrevented) onClose(); }; window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="gull fixed inset-0 z-[80] grid place-items-center overflow-y-auto bg-gray-900/40 backdrop-blur-[2px] p-3 sm:p-6" style={{ fontFamily: "Nunito, 'Segoe UI', sans-serif" }} onClick={onClose}>
      <div data-testid={testId} className={`tp-pop w-full ${wide ? "max-w-4xl" : "max-w-2xl"} max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-3rem)] overflow-y-auto rounded-xl border border-gray-100 bg-white text-gray-800 shadow-2xl`} onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white rounded-t-xl px-4 sm:px-5 py-3.5 border-b border-gray-100 text-base font-bold text-gray-800 flex items-center gap-3"><span className="flex-1 min-w-0">{title}</span>{onClose && <button type="button" data-testid={testId ? `${testId}-close` : "modal-close"} onClick={onClose} aria-label="Close" className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"><X className="w-4 h-4" /></button>}</div>
        <div className="p-4 sm:p-5">{children}</div>
      </div>
    </div>,
    document.body
  );
};

const BADGE = {
  Finalized: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", Sold: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", Active: "bg-sky-50 text-sky-700 ring-sky-600/20",
  "In Progress": "bg-amber-50 text-amber-700 ring-amber-600/20", "In Stock": "bg-amber-50 text-amber-700 ring-amber-600/20", Returned: "bg-gray-100 text-gray-600 ring-gray-500/20",
  Discontinued: "bg-red-50 text-red-700 ring-red-600/20", Ending: "bg-amber-50 text-amber-700 ring-amber-600/20",
  Open: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", Paid: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", Unpaid: "bg-red-50 text-red-700 ring-red-600/20", Partial: "bg-amber-50 text-amber-700 ring-amber-600/20", Overdue: "bg-red-50 text-red-700 ring-red-600/20",
};
export const StatusBadge = ({ status, label }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ring-inset ${BADGE[status] || "bg-gray-100 text-gray-600 ring-gray-500/20"}`}>
    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />{label || status}
  </span>
);

/* Underline tabs */
export const Tabs = ({ tabs, value, onChange, testPrefix = "tab" }) => (
  <div className="flex gap-1 border-b border-gray-200 mb-4 overflow-x-auto">
    {tabs.map(([k, l]) => (
      <button key={k} type="button" data-testid={`${testPrefix}-${k}`} onClick={() => onChange(k)}
        className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 whitespace-nowrap transition-colors ${value === k ? "border-[#663399] text-[#663399]" : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300"}`}>{l}</button>
    ))}
  </div>
);
